import { ApiClient, ApiClientConfig } from "../api-client";
import { ApiClientError } from "../errors";
import schemaValidators from "./schema-validators";

export type CourseTableEntry = {
	courseId: number;
	courseName: string;
	courseCode: string;
	studentCount: number;
	flaggedEvents: number;
	severityLevel: number;
};

export type AssessmentTableEntry = {
	quizId: number;
	quizName: string;
	studentCount: number;
	flaggedEvents: number;
	severityLevel: number;
};

export type StatsCardsData = {
	assessments?: { total: number; month: number };
	students?: { total: number; month: number };
	veritySessions?: { total: number; month: number };
	liveSessions?: { total: number; month: number };
	proctoringHours?: { total: number; month: number };
	liveHours?: { total: number; month: number };
	averageSessions?: { total: number; change: number };
	averageHours?: { total: number; change: number };
};

export type ReportResult = StatsCardsData & {
	monthlyReport: {
		totalVeritySessions: number[];
		totalLiveSessions: number[];
		totalLiveCheckInSessions: number[];
		totalNonLiveSessions: number[];
	};
	severityCounts: {
		none: number;
		minor: number;
		major: number;
		severe: number;
	};
	platformCounts?: {
		noneCount: number;
		lockdownCount: number;
		mobileCount: number;
		extensionCount: number;
	};
	courseTableReport?: CourseTableEntry[];
	assessmentTableReport?: AssessmentTableEntry[];
};

export type SubAccountNode = {
	id: number;
	accountName: string;
	lmsAccountId: string;
	parentLmsAccountId: string | null;
	rootLmsAccountId: string | null;
	children: SubAccountNode[];
};

export default class InstituteClient extends ApiClient<typeof schemaValidators> {
	constructor(config: ApiClientConfig) {
		super(config, schemaValidators);
	}

	getInstituteReport = async ({
		instituteId,
		subAccountId,
		startDate,
		endDate,
		monthYearRange,
	}: {
		instituteId: number;
		subAccountId?: number;
		startDate: Date;
		endDate: Date;
		monthYearRange: number[];
	}) => {
		try {
			const params = new URLSearchParams({
				instituteId: instituteId.toString(),
				...(subAccountId ? { subAccountId: subAccountId.toString() } : {}),
				startDate: startDate.getTime().toString(),
				endDate: endDate.getTime().toString(),
				monthYearRange: JSON.stringify(monthYearRange),
				timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
			});

			const url = new URL(`/api/verity/report/admin/institute-report`, this.config.verityOrigin);
			url.search = params.toString();
			const res = await super.fetch(url);
			if (res.ok) {
				return (await res.json()) as ReportResult;
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	getSubAccounts = async () => {
		try {
			const url = new URL(`/api/verity/report/admin/subaccounts`, this.config.verityOrigin);
			const res = await super.fetch(url);
			if (res.ok) {
				return (await res.json()) as { subAccounts: SubAccountNode[] };
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	getReceiveInstitutionReportEmail = async () => {
		try {
			const url = new URL(`/api/verity/report/admin/receive-institution-report-email`, this.config.verityOrigin);
			const res = await super.fetch(url);
			if (res.ok) {
				return (await res.json()) as boolean;
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};

	updateReceiveInstitutionReportEmail = async (receiveEmail: boolean) => {
		try {
			const url = new URL(`/api/verity/report/admin/receive-institution-report-email`, this.config.verityOrigin);
			const res = await super.fetch(url, {
				method: "POST",
				body: JSON.stringify({ receiveEmail: receiveEmail }),
				headers: { "Content-Type": "application/json" },
			});
			if (res.ok) {
				return;
			}
		} catch (error) {
			if (error instanceof Error) {
				throw new ApiClientError(error);
			}
		}
		throw new ApiClientError();
	};
}
